'use client';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Header, Onboarding, toast, useModal } from '@ohif/ui-next';
import { SidebarNavigation } from '@ohif/ui';
import { useAppConfig } from '@state';
import PropTypes from 'prop-types';

// Importaciones del servicio de Sucursales y Organizaciones
import { fetchAllOrganizations } from '../Organizations/organization'; // Para el selector de organización
import {
  fetchAllBranches,
  createBranch,
  updateBranch,
  deleteBranch,
  BranchAPI,
  BranchData,
} from './branch'; // Asegúrate de la ruta correcta

// Componentes Separados
import BranchFormModal from './components/BranchFromModal';
import BranchList from './components/BranchList';
import { getHeaderConfig } from '../Users/Users';

// --- Estado Inicial del Formulario ---
const INITIAL_FORM_DATA: BranchData = {
  fk_organization: '', // ID de la organización seleccionada
  name: '',
  short_name: '',
  oid: '',
  country_code: '',
  structure_id: '',
  suffix: '',
  base64_logo: '',
};

function BranchPanel({ servicesManager }) {
  const [branches, setBranches] = useState<BranchAPI[]>([]);
  const [organizations, setOrganizations] = useState<{ _id: string; name: string }[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingBranch, setEditingBranch] = useState<BranchAPI | null>(null);
  const [formData, setFormData] = useState<BranchData>(INITIAL_FORM_DATA);
  const [isLoading, setIsLoading] = useState(false);

  const navigate = useNavigate();
  const { t } = useTranslation();
  const { show } = useModal();
  const [appConfig] = useAppConfig();

  const { menuOptions } = getHeaderConfig({ servicesManager, t, show, navigate, appConfig });

  // --- Lógica de Carga de Datos ---

  // 1. Cargar Organizaciones (necesario para el selector en el formulario)
  const loadOrganizations = async () => {
    try {
      const apiOrganizations = await fetchAllOrganizations();
      // Mapear a un formato simple para el selector
      setOrganizations(apiOrganizations.map(org => ({ _id: org._id, name: org.name })));
    } catch (error) {
      toast.error('Error al obtener la lista de organizaciones.');
    }
  };

  // 2. Cargar Sucursales
  const loadBranches = async () => {
    try {
      setIsLoading(true);
      const apiBranches = await fetchAllBranches();
      setBranches(apiBranches);
    } catch (error) {
      toast.error('Error al obtener sucursales: ' + (error as any).message);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    // Verificar sesión (omitiendo el código para mantener la concisión)
    // Luego cargar datos:
    loadOrganizations();
    loadBranches();
  }, []);

  // --- Lógica de Filtro ---
  const filteredBranches = branches.filter(
    (branch) =>
      branch.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      branch.oid.toLowerCase().includes(searchTerm.toLowerCase()) ||
      branch.fk_organization.name.toLowerCase().includes(searchTerm.toLowerCase()) // Busca por nombre de organización
  );

  // --- Manejadores CRUD ---

  const handleCreateBranch = async () => {
    try {
      const response = await createBranch(formData);
      toast.success(`Sucursal ${response.name} creada exitosamente.`);
      await loadBranches();
      resetForm();
    } catch (err: any) {
      toast.error('Error al crear sucursal: ' + (err.message || err));
    }
  };

  const handleUpdateBranch = async () => {
    if (!editingBranch) return;
    try {
      // No permitimos cambiar la organización padre (`fk_organization`) ni el `oid` al editar.
      const updateData: Partial<BranchData> = {
        name: formData.name,
        short_name: formData.short_name,
        country_code: formData.country_code,
        structure_id: formData.structure_id,
        suffix: formData.suffix,
        base64_logo: formData.base64_logo,
      };

      const response = await updateBranch(editingBranch._id, updateData);
      toast.success(`Sucursal ${response.name} actualizada.`);
      await loadBranches();
      resetForm();
    } catch (err: any) {
      toast.error('Error al actualizar sucursal: ' + (err.message || err));
    }
  };

  const handleDeleteBranch = async (id: string) => {
    if (!window.confirm('¿Estás seguro de que quieres eliminar esta sucursal?')) {
      return;
    }
    try {
      await deleteBranch(id);
      toast.success('Sucursal eliminada correctamente.');
      setBranches((prev) => prev.filter((b) => b._id !== id));
    } catch (err: any) {
      toast.error('Error al eliminar la sucursal: ' + (err.message || err));
    }
  };

  const handleEditBranch = (branch: BranchAPI) => {
    setEditingBranch(branch);
    // Mapear BranchAPI a BranchData para el formulario
    setFormData({
      fk_organization: branch.fk_organization._id, // Usamos el ID de la organización
      name: branch.name,
      short_name: branch.short_name,
      oid: branch.oid,
      country_code: branch.country_code,
      structure_id: branch.structure_id,
      suffix: branch.suffix,
      base64_logo: branch.base64_logo || '',
    });
    setIsDialogOpen(true);
  };

  const resetForm = () => {
    setFormData(INITIAL_FORM_DATA);
    setEditingBranch(null);
    setIsDialogOpen(false);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    editingBranch ? handleUpdateBranch() : handleCreateBranch();
  };

  return (
    <div className="flex h-full flex-col bg-black">
      <Header
        isSticky
        menuOptions={menuOptions}
        isReturnEnabled={false}
        WhiteLabeling={appConfig.whiteLabeling}
      />
      <Onboarding />
      <SidebarNavigation onFilterToggle={() => navigate('/')} />

      <div className="container mx-auto min-h-screen bg-gray-50 pt-5 text-white max-sm:p-4 sm:pl-9">
        {/* Header del Panel */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">Gestión de Sucursales</h1>
            <p className="mt-1">Administra las sedes y filiales de las organizaciones</p>
          </div>
          <button
            onClick={() => {
              setEditingBranch(null);
              setFormData(INITIAL_FORM_DATA);
              setIsDialogOpen(true);
            }}
            className="bg-secondary-dark hover:bg-primary-dark flex items-center rounded px-4 py-2 text-white"
          >
            Crear Sucursal
          </button>
        </div>

        {/* Lista de Sucursales */}
        <BranchList
          branches={filteredBranches}
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          isLoading={isLoading}
          handleEditBranch={handleEditBranch}
          handleDeleteBranch={handleDeleteBranch}
        />
      </div>

      {/* Modal de Formulario */}
      <BranchFormModal
        isDialogOpen={isDialogOpen}
        editingBranch={editingBranch}
        formData={formData}
        setFormData={setFormData}
        handleSubmit={handleSubmit}
        resetForm={resetForm}
        organizations={organizations}
      />
    </div>
  );
}

BranchPanel.propTypes = {
  servicesManager: PropTypes.object.isRequired,
};

export default BranchPanel;
